import { supabase, verifyPassword } from './supabase'
import { hasEmailPassword, passwordGate } from './auth-guard'
import * as db from './db'
import { dayKey, secOfDay } from './time'
import { downloadFile } from './util'
import {
  backupFilename,
  type BackupFile,
  type TableResult,
} from '../../supabase/functions/_shared/backup'
import type {
  Appearance,
  AuthConfig,
  AuthPayload,
  ClientKind,
  DayKey,
  Entry,
  ID,
  Role,
  Settings,
  Workspace,
} from './types'

/**
 * The API surface the app has always used, now backed by Supabase.
 *
 * The method names, arguments and return types are unchanged on purpose. The
 * old Express API answered every mutation with the whole fresh workspace
 * snapshot and the screens were built around that, so each call here performs
 * its write and then reloads the snapshot. Same contract, different engine --
 * which is why AppContext and the nine screens needed almost no changes.
 */

/** Thrown by login when a verified TOTP code is still needed. */
export const MFA_REQUIRED = 'MFA_REQUIRED'

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message)
  }
}

/* ------------------------------------------------------------------ */
/* Session                                                             */
/* ------------------------------------------------------------------ */

let cachedUserId: ID | null = null

async function requireUserId(): Promise<ID> {
  if (cachedUserId) return cachedUserId
  const { data } = await supabase.auth.getSession()
  const id = data.session?.user.id
  if (!id) throw new ApiError('Your session has expired. Please sign in again.', 401)
  cachedUserId = id
  return id
}

/** Reload the snapshot after a write, so callers keep their old contract. */
async function snap(workspaceId: ID): Promise<Workspace> {
  return db.loadWorkspace(workspaceId, await requireUserId())
}

/**
 * Supabase reports a blocked write as an RLS failure, which is accurate and
 * unreadable. Translate the ones a person can actually act on.
 */
function humanise(error: unknown): never {
  const message = error instanceof Error ? error.message : String(error)

  if (/row-level security|violates row-level/i.test(message)) {
    throw new ApiError('You do not have permission to do that.', 403)
  }
  // Reached when an entry is added or edited by hand beyond the limit. The
  // running-timer path no longer gets here at all: stop_timer caps instead.
  if (/time_entries_max_length|pending_max_length/i.test(message)) {
    throw new ApiError('An entry cannot be longer than 10 hours.', 400)
  }
  if (/cannot end in the future/i.test(message)) {
    throw new ApiError('An entry cannot end in the future.', 400)
  }
  if (/last active admin/i.test(message)) {
    throw new ApiError('That is the last active admin — promote someone else first.', 409)
  }
  if (/duplicate key|already exists/i.test(message)) {
    throw new ApiError('That name is already taken.', 409)
  }
  if (/Failed to fetch|NetworkError/i.test(message)) {
    throw new ApiError('Can’t reach the server. Check your connection.', 0)
  }
  throw new ApiError(message, 400)
}

/** Run a write, reload the snapshot, and turn database errors into English. */
async function write(workspaceId: ID, fn: () => Promise<unknown>): Promise<Workspace> {
  try {
    await fn()
    return await snap(workspaceId)
  } catch (err) {
    humanise(err)
  }
}

/**
 * Call an Edge Function and surface the message it actually sent.
 *
 * supabase-js reports every non-2xx as the same FunctionsHttpError -- "Edge
 * Function returned a non-2xx status code" -- and hides the real body on
 * `error.context`, which is the raw Response. Without unwrapping it, a
 * validation message the function took care to write never reaches the person
 * who needs to read it.
 */
async function invokeFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body })
  if (!error) return data as T

  const response = (error as { context?: unknown }).context
  if (response instanceof Response) {
    try {
      const text = await response.text()
      try {
        const parsed = JSON.parse(text) as { error?: string }
        if (parsed.error) throw new ApiError(parsed.error, response.status)
      } catch (parseErr) {
        if (parseErr instanceof ApiError) throw parseErr
        if (text.trim()) throw new ApiError(text.trim().slice(0, 300), response.status)
      }
    } catch (err) {
      if (err instanceof ApiError) throw err
      // Anything else is a fault in the unwrapping above rather than a
      // response from the function. It used to vanish here and come back as a
      // bare 500, which hid the actual cause from whoever had to fix it.
      if (import.meta.env.DEV) console.error('Could not read the error response', err)
    }
  }

  throw new ApiError(error.message, 500)
}

/* ------------------------------------------------------------------ */
/* Entry helpers the timesheet needs                                   */
/* ------------------------------------------------------------------ */

/**
 * The caller's own entries.
 *
 * The snapshot is scoped to you, so this is your time and nobody else's --
 * which is all the timesheet writes below act on. Anything that needs the
 * team's time goes through db.listEntries with a date range instead.
 */
async function entriesFor(workspaceId: ID): Promise<Entry[]> {
  const ws = await snap(workspaceId)
  return ws.entries
}

/** One person named in an import file, and what will happen to their rows. */
export interface ImportPerson {
  email: string
  name: string
  matched: boolean
  matchedName: string | null
  decision: 'member' | 'park' | 'skip'
  userId: ID | null
  rows: number
}

/** What a preview promises, and what a commit reports having done. */
export interface ImportReport {
  mode: 'preview' | 'commit'
  rows: number
  issues: { line: number; message: string }[]
  currency: string | null
  clients: { name: string; exists: boolean }[]
  projects: { name: string; client: string; exists: boolean }[]
  tasks: { project: string; name: string }[]
  people: ImportPerson[]
  duplicates: number
  imported: number
  parked: number
  skipped: number
  batchId?: ID
  created?: { clients: number; projects: number; tasks: number }
}

/** 'park' holds the rows until that person has an account; 'skip' drops them. */
export type ImportDecision = ID | 'park' | 'skip'

/** The grouping levels the summary export understands. */
export type ExportGroupKey = 'client' | 'project' | 'description' | 'task' | 'user'

export interface RestoreReport {
  ok: boolean
  /** Which kind of file it was. A file written before scopes is 'standard'. */
  scope?: 'standard' | 'complete'
  /** Whether the workspace's own settings were put back. Complete files only. */
  settingsRestored?: boolean
  takenAt: string
  workspaceName: string
  restored: number
  failed: number
  tables: TableResult[]
  note: string
}

export interface ExportQuery {
  from: DayKey
  to: DayKey
  scope: 'mine' | 'all'
  /**
   * Which population the file covers. Omitted means clients, which is what
   * the Reports screen asks for and what finance expects: prospect time is
   * excluded from it entirely, and is only ever exported on its own.
   */
  kind?: ClientKind
  userId?: ID | null
  /** Several people. Takes precedence over userId; empty means everyone. */
  userIds?: readonly ID[] | null
  clientId?: ID | null
  projectId?: ID | null
  /**
   * Several clients or projects, as userIds is for people.
   *
   * Takes precedence over the single-id fields, and empty means all of them
   * -- the same convention the filter bar uses, so an untouched picker and a
   * cleared one are the same query.
   */
  clientIds?: readonly ID[] | null
  projectIds?: readonly ID[] | null
  /**
   * Narrow to these pricing models, as the view names them.
   *
   * The screen decides what counts -- see FIXED_FEE_MODELS -- and sends the
   * list, so the file and the report it was taken from cannot disagree about
   * what a fixed fee is.
   */
  pricingModels?: readonly string[] | null
  billable?: boolean | null
  /** 'detailed' is the per-entry file; 'summary' is the grouped VP layout. */
  layout?: 'detailed' | 'summary'
  /** Summary only -- the detailed layout ignores it. */
  groupBy?: ExportGroupKey[]
}

export interface EntryInput {
  /** Omitted entirely by anyone who may not price an entry; see guard_entry_pricing. */
  rateOverride?: number | null
  fixedAmount?: number | null
  desc: string
  projectId: ID | null
  task?: string
  tags?: string[]
  billable: boolean
  day: DayKey
  start: number
  dur: number
  userId?: ID
}

/* ------------------------------------------------------------------ */

export const api = {
  /* ---- auth ---- */

  config: async (): Promise<AuthConfig> => ({
    // Google sign-in was removed from the sign-in screen. The flag stays so
    // the shape is unchanged, but nothing renders it; turning it back on
    // means restoring the button, not flipping this.
    google: false,
    googleDomain: null,
    passwordLogin: true,
  }),

  me: async (): Promise<AuthPayload> => {
    const { data } = await supabase.auth.getSession()
    const user = data.session?.user
    if (!user) throw new ApiError('Not signed in', 401)
    cachedUserId = user.id

    // The error matters: a failed read is not the same as "no profile row",
    // and treating them alike silently skips the deactivation check below.
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('email, full_name, is_active')
      .eq('id', user.id)
      .maybeSingle()

    if (profileError) {
      throw new ApiError(`Could not load your account: ${profileError.message}`, 503)
    }

    // A deactivated person is signed out rather than left holding a session
    // that RLS will refuse at every turn (A7).
    if (profile && !profile.is_active) {
      await supabase.auth.signOut()
      cachedUserId = null
      throw new ApiError('Your account has been deactivated. Contact an administrator.', 403)
    }

    const workspaces = await db.listWorkspaces(user.id)

    return {
      user: {
        id: user.id,
        email: profile?.email ?? user.email ?? '',
        name: profile?.full_name || (user.email ?? '').split('@')[0],
        // Read from the identities, not assumed. This said `true` for
        // everybody, which put a "Current password" box in front of people
        // who sign in with Google and have no password to type -- harmless
        // while the box was ignored, and a lock-out the moment it is not.
        hasPassword: hasEmailPassword(user.identities),
        viaGoogle: user.app_metadata?.provider === 'google',
      },
      workspaces,
      // Invites were replaced by request-and-approve; a pending request is
      // surfaced by the sign-in flow, not here.
      invites: [],
    }
  },

  login: async (b: { email: string; password: string }): Promise<AuthPayload> => {
    const { error } = await supabase.auth.signInWithPassword({
      email: b.email.trim(),
      password: b.password,
    })

    if (error) {
      // An unconfirmed address is called out by name. Collapsing it into
      // "wrong password" is technically the safer answer, but it sends
      // somebody to reset a password that was never the problem -- and the
      // confirmation mail already proves the account exists to anyone holding
      // the address. Everything else stays deliberately vague so the form
      // cannot be used to discover who has an account (A1).
      if (/confirm/i.test(error.message)) {
        throw new ApiError(
          'Your email address has not been confirmed yet. Check your inbox for the confirmation link, or ask an administrator to confirm you.',
          403,
        )
      }
      throw new ApiError('That email and password don’t match.', 401)
    }

    // The password is only the first factor. If this account has TOTP, the
    // session is aal1 and stays useless until a code is verified -- so stop
    // here rather than returning a payload that looks like a completed
    // sign-in.
    if (await api.mfa.stepUpRequired()) {
      throw new ApiError(MFA_REQUIRED, 428)
    }

    cachedUserId = null
    return api.me()
  },

  register: async (input: {
    email: string
    name: string
    password: string
  }): Promise<AuthPayload> => {
    const { data, error } = await supabase.auth.signUp({
      email: input.email.trim(),
      password: input.password,
      options: { data: { full_name: input.name.trim() } },
    })
    if (error) throw new ApiError(error.message, 400)

    /*
     * Signing up with an address that already has an account is answered with
     * a decoy: a user object carrying no identities, and no mail sent. That is
     * deliberate -- it stops the form being used to discover who is
     * registered -- but it is shaped exactly like success, so the screen said
     * "check your email" for a message that was never going to arrive, and
     * the administrator waited for an approval that was never filed.
     *
     * An empty identities array is the one thing that distinguishes it.
     */
    if (data.user && (data.user.identities?.length ?? 0) === 0) {
      throw new ApiError(
        'That address already has an account. Sign in instead, or use “Forgot your password?” if you have lost the password.',
        409,
      )
    }

    /*
     * With confirmations on, sign-up returns no session -- saying so is the
     * whole message, because calling me() here would fail with "not signed
     * in" and read as though the sign-up itself broke.
     *
     * It no longer tells them to go and confirm anything. Approval records
     * the confirmation, so the account is unblocked by an admin pressing
     * Approve rather than by an email arriving. Telling somebody to wait for
     * a message they do not need is how you get a person sitting on a link
     * that never came, for a step that had already been taken care of.
     */
    if (!data.session) {
      throw new ApiError(
        'Your request has been sent. Once an administrator approves it you can sign in with the password you just chose.',
        202,
      )
    }

    cachedUserId = null
    return api.me()
  },

  logout: async () => {
    await supabase.auth.signOut()
    cachedUserId = null
    return { ok: true } as const
  },

  /** Signs out everywhere, not just this browser (A6). */
  logoutEverywhere: async () => {
    await supabase.auth.signOut({ scope: 'global' })
    cachedUserId = null
    return { ok: true } as const
  },

  /**
   * Send the "set a new password" link.
   *
   * The result used to be thrown away and { ok: true } returned whatever
   * happened, on the reasoning that a uniform answer stops the form being
   * used to discover who has an account. The first half of that is right and
   * the second half does not follow: GoTrue answers an UNKNOWN address with
   * success, so it never errors for that reason. Everything that does reach
   * here -- rate limiting, a refused redirect, SMTP failing -- is a fault,
   * and swallowing it told somebody a mail was on its way when none was sent.
   *
   * So: unknown addresses still look exactly like known ones, and a genuine
   * failure is reported.
   */
  resetPassword: async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/set-password`,
    })

    if (!error) return { ok: true } as const

    return {
      ok: false,
      problem:
        error.status === 429
          ? 'Too many attempts just now. Wait a minute and try again.'
          : 'The email could not be sent: ' + error.message,
    } as const
  },

  updateProfile: async (b: {
    name?: string
    password?: string
    currentPassword?: string
  }): Promise<AuthPayload> => {
    const userId = await requireUserId()

    /*
     * The password is settled BEFORE anything is written.
     *
     * The name used to be saved first, so a rejected password left the name
     * already changed and the person looking at an error. Half a save is bad
     * anywhere; it is worse when the half that failed is a security check.
     *
     * currentPassword was accepted here and never looked at. The screen asked
     * for it, refused to continue without it, and then any text at all let a
     * new password through -- so anyone holding a live session could change
     * the password without knowing the old one, which is the one thing that
     * field exists to stop.
     */
    if (b.password) {
      const { data, error: whoError } = await supabase.auth.getUser()
      if (whoError || !data.user) {
        throw new ApiError('Sign in again before changing your password.', 401)
      }

      const gate = passwordGate(hasEmailPassword(data.user.identities), b.currentPassword)

      if (gate.kind === 'need-current') {
        throw new ApiError('Enter your current password to change it.', 400)
      }

      if (gate.kind === 'verify') {
        const verdict = await verifyPassword(data.user.email ?? '', gate.password)

        if (verdict === 'wrong') {
          throw new ApiError('That is not your current password.', 401)
        }

        // Could not check is NOT the same as checked and wrong. Refusing is
        // the only safe reading: waving the change through whenever the
        // check is rate limited or the service is down would reopen the hole
        // for anyone willing to trip the rate limit first.
        if (verdict === 'problem') {
          throw new ApiError(
            'Could not confirm your current password just now. Try again in a moment.',
            503,
          )
        }
      }
    }

    if (b.name !== undefined) {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: b.name })
        .eq('id', userId)
      if (error) humanise(error)
    }

    if (b.password) {
      const { error } = await supabase.auth.updateUser({ password: b.password })
      if (error) throw new ApiError(error.message, 400)
    }

    return api.me()
  },

  /**
   * The caller's own access request, if any.
   *
   * The filter is not optional, whatever the old comment here claimed. The
   * policy is `user_id = auth.uid() OR is_admin(workspace_id)` -- it shows an
   * admin every request in their workspace, which is exactly what the
   * approval queue needs and exactly wrong here. Without the filter this
   * returned the most recent request from ANYBODY, so an admin could be told
   * they were waiting for approval on the strength of a stranger's sign-up.
   */
  myAccessRequest: async (): Promise<{ status: string } | null> => {
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('access_requests')
      .select('status')
      .eq('user_id', userId)
      .order('requested_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    // A failed read is not "no request": reporting it as one drops somebody
    // who is genuinely waiting onto the create-a-workspace screen instead.
    if (error) throw new ApiError(error.message, 503)
    return data ?? null
  },

  /* ---- two-factor ---- */

  mfa: {
    /**
     * True when this session has a factor enrolled but has not yet been
     * raised to aal2. Supabase reports that as current 'aal1' with a next
     * level of 'aal2'; anything else means there is nothing to do.
     */
    stepUpRequired: async (): Promise<boolean> => {
      /*
       * Fails closed, and asks the right question.
       *
       * This used to read the assurance level alone and return false on any
       * error -- so a network blip while signing in was indistinguishable
       * from "this account has no second factor", and the code was never
       * asked for. A security control that skips itself when something goes
       * wrong is not a control.
       *
       * The enrolled factors decide, matching the documented flow: sign in,
       * list the factors, and if there are any, demand a code. An assurance
       * level that cannot be read is not evidence there is nothing to ask
       * for, so that failure means "ask" too.
       */
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) {
        throw new ApiError('Could not check two-factor authentication. Try again.', 503)
      }

      const verified = (data?.totp ?? []).filter((f) => f.status === 'verified')
      if (verified.length === 0) return false

      const { data: level } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      return level?.currentLevel !== 'aal2'
    },

    /** Factors that finished enrolment. An abandoned one is not protection. */
    listVerified: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) throw new ApiError(error.message, 400)
      return (data?.totp ?? []).filter((f) => f.status === 'verified')
    },

    /**
     * Start enrolment. Returns the QR code and the secret to type by hand --
     * both are needed, since plenty of people scan and plenty cannot.
     */
    enroll: async () => {
      /*
       * Clear out abandoned attempts first.
       *
       * Starting enrolment creates a factor immediately and leaves it
       * 'unverified' until a code is entered. Closing the panel at the QR
       * code keeps it. The project allows two factors per person, so two
       * changes of mind are enough to hit the ceiling and be told the
       * maximum is reached -- with nothing in the interface admitting the
       * two blocking it were never finished.
       *
       * An unverified factor is not protection, so removing them costs
       * nothing and cannot lock anybody out of a factor they rely on.
       */
      const { data: existing } = await supabase.auth.mfa.listFactors()
      for (const factor of existing?.all ?? []) {
        if (factor.status !== 'verified') {
          await supabase.auth.mfa.unenroll({ factorId: factor.id })
        }
      }

      // Supabase rejects a duplicate friendly name, and an abandoned
      // enrolment keeps its name, so make it unique.
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Cadence ${new Date().toISOString().slice(0, 19)}`,
      })
      if (error) throw new ApiError(error.message, 400)
      return {
        factorId: data.id,
        qr: data.totp.qr_code,
        secret: data.totp.secret,
      }
    },

    /** Finish enrolment, or satisfy a step-up at sign-in. Same call either way. */
    verify: async (factorId: ID, code: string) => {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: code.replace(/\s+/g, ''),
      })
      if (error) throw new ApiError('That code was not accepted. Codes expire after 30 seconds.', 401)
      cachedUserId = null
    },

    /** Satisfy a step-up when the factor is whichever verified one exists. */
    verifyPending: async (code: string) => {
      const factors = await api.mfa.listVerified()
      const factor = factors[0]
      if (!factor) throw new ApiError('No two-factor app is set up on this account.', 400)
      await api.mfa.verify(factor.id, code)
    },

    unenroll: async (factorId: ID) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId })
      if (error) throw new ApiError(error.message, 400)
    },
  },

  /* ---- workspaces ---- */

  createWorkspace: async (name: string): Promise<Workspace> => {
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('workspaces')
      .insert({ name, owner_id: userId })
      .select('id')
      .single()
    if (error) humanise(error)
    return db.loadWorkspace(data.id, userId)
  },

  // Join codes were replaced by request-and-approve. Kept so the existing
  // screen still compiles; it reports the change rather than failing oddly.
  joinWorkspace: async (_code: string): Promise<Workspace> => {
    throw new ApiError(
      'Joining by code has been replaced. Sign up and an administrator will approve you.',
      400,
    )
  },

  acceptInvite: async (_inviteId: ID): Promise<Workspace> => {
    throw new ApiError('Invites have been replaced by administrator approval.', 400)
  },

  workspace: async (id: ID): Promise<Workspace> => {
    try {
      return await snap(id)
    } catch (err) {
      humanise(err)
    }
  },

  updateWorkspace: (
    id: ID,
    b: { name?: string; settings?: Partial<Settings>; acceptsSignups?: boolean },
  ) =>
    write(id, () => db.updateWorkspace(id, b)),

  updateAppearance: (id: ID, appearance: Partial<Appearance>) =>
    write(id, async () => db.updateAppearance(id, await requireUserId(), appearance)),

  regenerateCode: async (_id: ID): Promise<Workspace> => {
    throw new ApiError('Join codes are no longer used.', 400)
  },

  leaveWorkspace: async (id: ID) => {
    const userId = await requireUserId()
    try {
      await db.removeMember(id, userId)
    } catch (err) {
      humanise(err)
    }
    return { ok: true } as const
  },

  deleteWorkspace: async (id: ID) => {
    const { error } = await supabase.from('workspaces').delete().eq('id', id)
    if (error) humanise(error)
    return { ok: true } as const
  },

  /* ---- people ---- */

  /**
   * Returns the snapshot AND the sign-in link, because email delivery is the
   * least reliable part of this and the admin needs a way through regardless.
   */
  invite: async (
    id: ID,
    b: { email: string; role: Role; capacity: number },
  ): Promise<{
    ws: Workspace
    actionLink: string | null
    emailed: boolean
    emailProblem: string | null
    linkProblem: string | null
  }> => {
    const result = await invokeFunction<{
      actionLink: string | null
      emailed: boolean
      emailProblem: string | null
      linkProblem: string | null
    }>('invite-user', {
      workspaceId: id,
      email: b.email,
      role: b.role,
      capacity: b.capacity,
    })

    return {
      ws: await snap(id),
      actionLink: result?.actionLink ?? null,
      emailed: Boolean(result?.emailed),
      emailProblem: result?.emailProblem ?? null,
      linkProblem: result?.linkProblem ?? null,
    }
  },

  cancelInvite: async (id: ID, _inviteId: ID): Promise<Workspace> => snap(id),

  updateMember: (id: ID, userId: ID, b: { role?: Role; capacity?: number }) =>
    write(id, () => db.updateMember(id, userId, b)),

  /**
   * Removing somebody frees their address so they can sign up again, which
   * means touching the auth account -- service-role work, so it goes through
   * the Edge Function rather than straight at the table.
   */
  removeMember: (id: ID, userId: ID) =>
    write(id, () => invokeFunction('retire-member', { workspaceId: id, userId })),

  /** Undoes removeMember: back in the workspace, able to sign in again. */
  restoreMember: (id: ID, userId: ID) =>
    write(id, () => invokeFunction('restore-member', { workspaceId: id, userId })),

  /**
   * A one-time password link for somebody on the team, handed to the owner
   * rather than emailed.
   *
   * Not wrapped in write(): it changes nothing, so there is nothing for the
   * screen to reload afterwards. The origin goes with it because the link has
   * to point back at wherever this app is actually being served.
   */
  resetLinkFor: (id: ID, userId: ID) =>
    invokeFunction<{ url: string; email: string; name: string }>('reset-link', {
      workspaceId: id,
      userId,
      origin: window.location.origin,
    }),

  /* ---- clients ---- */

  createClient: (id: ID, b: { name: string; contact: string }) =>
    write(id, () => db.createClient(id, b)),
  updateClient: (id: ID, clientId: ID, b: db.ClientPatch) =>
    write(id, () => db.updateClient(clientId, b)),
  /*
   * Deleting a client sends it to the bin; the bin is the only place it can
   * be deleted for good. Named for what each one does rather than keeping
   * `deleteClient` for the soft one -- a call that reads as delete and is
   * not, or reads as delete and is, is exactly the pair worth spelling out.
   */
  binClient: (id: ID, clientId: ID) => write(id, () => db.binClient(clientId)),
  restoreClient: (id: ID, clientId: ID, name: string) =>
    write(id, () => db.restoreClient(clientId, name)),
  purgeClient: (id: ID, clientId: ID) => write(id, () => db.purgeClient(clientId)),

  /* ---- projects ---- */

  createProject: (id: ID, b: Record<string, unknown>) => write(id, () => db.createProject(id, b)),
  updateProject: (id: ID, projectId: ID, b: db.ProjectPatch) =>
    write(id, () => db.updateProject(projectId, b)),
  deleteProject: (id: ID, projectId: ID) => write(id, () => db.deleteProject(projectId)),

  /* ---- tags ---- */

  addTag: (id: ID, name: string) => write(id, () => db.addTag(id, name)),
  removeTag: (id: ID, name: string) => write(id, () => db.removeTag(id, name)),

  /* ---- entries ---- */

  createEntry: (id: ID, b: EntryInput) =>
    write(id, async () => db.createEntry(id, await requireUserId(), b)),

  updateEntry: (id: ID, entryId: ID, b: Partial<EntryInput>) =>
    write(id, async () => {
      // Read the one row rather than hunting the snapshot: an admin editing
      // somebody else's entry will not find it there.
      const current = await db.getEntry(entryId)
      if (!current) throw new Error('Entry not found')
      await db.updateEntry(id, entryId, current, b)
    }),

  deleteEntry: (id: ID, entryId: ID) => write(id, () => db.deleteEntry(entryId)),

  wipeEntries: (id: ID, scope: 'mine' | 'all') =>
    write(id, async () =>
      db.wipeEntries(id, scope === 'all' ? null : await requireUserId()),
    ),

  /* ---- timesheet ---- */

  setCell: (id: ID, b: { projectId: ID; day: DayKey; seconds: number }) =>
    write(id, async () => {
      const userId = await requireUserId()
      await db.setCell(id, userId, b.projectId, b.day, b.seconds, await entriesFor(id))
    }),

  clearRow: (id: ID, b: { projectId: ID; days: DayKey[] }) =>
    write(id, async () => {
      const userId = await requireUserId()
      const all = await entriesFor(id)
      const doomed = all.filter(
        (e) => e.userId === userId && e.projectId === b.projectId && b.days.includes(e.day),
      )
      for (const entry of doomed) await db.deleteEntry(entry.id)
    }),

  copyWeek: async (id: ID, b: { from: DayKey[]; to: DayKey[] }) => {
    const userId = await requireUserId()
    const all = await entriesFor(id)
    let copied = 0
    let problem: string | null = null

    try {
      for (let i = 0; i < b.from.length && i < b.to.length; i++) {
        const source = all.filter((e) => e.userId === userId && e.day === b.from[i])
        for (const entry of source) {
          await db.createEntry(id, userId, {
            day: b.to[i],
            start: entry.start,
            dur: entry.dur,
            projectId: entry.projectId,
            desc: entry.desc,
            task: entry.task,
            tags: entry.tags,
            billable: entry.billable,
          })
          copied++
        }
      }
    } catch (err) {
      // Partial copies are kept: the days already written are real work. But
      // the reason the run stopped used to be discarded once anything had
      // been written, so "Copied 3 entries" was all you got whether the rest
      // were duplicates, a permission problem, or a dropped connection.
      if (copied === 0) humanise(err)
      problem = err instanceof Error ? err.message : 'The copy stopped early.'
    }

    return { ...(await snap(id)), copied, problem }
  },

  submitWeek: (id: ID, weekStart: DayKey) =>
    write(id, async () => db.submitWeek(id, await requireUserId(), weekStart)),

  unsubmitWeek: (id: ID, weekStart: DayKey) =>
    write(id, async () => db.unsubmitWeek(id, await requireUserId(), weekStart)),

  /* ---- timer ---- */

  startTimer: (
    id: ID,
    b: {
      desc: string
      projectId: ID | null
      task: string
      billable: boolean
      at?: number
      /** Priced as it starts, rather than corrected after it stops. */
      fixedAmount?: number | null
    },
  ) =>
    write(id, async () => {
      await db.startTimer(id, b)
      /*
       * A second write, because start_timer is an RPC with a fixed signature
       * and the fee is not one of its arguments. Inside the same write() so
       * the screen refreshes once, with both facts already true.
       */
      if (b.fixedAmount != null) {
        await db.updateRunning(id, await requireUserId(), { fixedAmount: b.fixedAmount })
      }
    }),

  updateTimer: (
    id: ID,
    b: Partial<{
      desc: string
      projectId: ID | null
      task: string
      billable: boolean
      /** Epoch ms. Moves when the running timer started. */
      at: number
      /** Null clears the fee and returns the entry to its usual rate. */
      fixedAmount: number | null
    }>,
  ) => write(id, async () => db.updateRunning(id, await requireUserId(), b)),

  /**
   * `endedAt` is an ISO instant for the midnight auto-stop. It used to be a
   * day, a start and a duration, none of which were read -- the parameter was
   * literally named `_q` -- while the RPC always ended the entry at server
   * time. The setting promised timers would stop at midnight and they stopped
   * whenever the tab next woke up.
   */
  stopTimerFor: (id: ID, userId: ID, endedAt?: string) =>
    write(id, () => db.stopTimerFor(id, userId, endedAt)),

  stopTimer: async (id: ID, endedAt?: string) => {
    const before = await snap(id)
    const userId = await requireUserId()
    const running = before.running[userId]

    try {
      await db.stopTimer(endedAt)
    } catch (err) {
      humanise(err)
    }

    const stoppedAt = endedAt ? Date.parse(endedAt) : Date.now()
    const savedDur = running ? Math.max(1, Math.round((stoppedAt - running.at) / 1000)) : 0
    return { ...(await snap(id)), savedDur }
  },

  /* ---- import ---- */

  /**
   * Preview and commit run the same parse on the server, so the file can
   * never do something other than what the preview said it would.
   */
  importCsv: async (
    id: ID,
    q: { csv: string; filename: string; mode: 'preview' | 'commit'; mapping?: Record<string, ImportDecision> },
  ): Promise<ImportReport> => {
    return await invokeFunction<ImportReport>('import-csv', {
      workspaceId: id,
      csv: q.csv,
      filename: q.filename,
      mode: q.mode,
      mapping: q.mapping ?? {},
    })
  },

  /** Hands parked rows to their person once they have an account here. */
  attachPending: async (id: ID, email: string): Promise<number> => {
    const { data, error } = await supabase.rpc('attach_pending_imports', {
      p_workspace: id,
      p_email: email,
    })
    if (error) humanise(error)
    return Number(data ?? 0)
  },

  /* ---- export ---- */

  /**
   * Was a GET URL on the old server; the export now runs in an Edge Function
   * that checks admin rights, so it is a POST whose body becomes a download.
   */
  /**
   * A full copy of the workspace, as a file the owner keeps.
   *
   * The function answers with the backup itself rather than a wrapper, so what
   * lands on disk is exactly what restore reads back. Stringified here with an
   * indent: a backup is a file somebody may need to look inside when something
   * has gone wrong, and the size difference is not worth the opacity.
   */
  backupWorkspace: async (
    id: ID,
    scope: 'standard' | 'complete' = 'standard',
  ): Promise<{ filename: string; json: string }> => {
    const file = await invokeFunction<BackupFile>('backup', {
      workspaceId: id,
      mode: 'export',
      scope,
    })
    return {
      filename: backupFilename(file.workspaceName ?? 'workspace', file.takenAt ?? ''),
      json: JSON.stringify(file, null, 2),
    }
  },

  /**
   * Merge a backup back in. Adds what is missing; never deletes, never
   * overwrites a row that is already there.
   */
  restoreWorkspace: async (id: ID, file: unknown): Promise<RestoreReport> =>
    await invokeFunction<RestoreReport>('backup', { workspaceId: id, mode: 'restore', file }),

  exportCsv: async (id: ID, q: ExportQuery) => {
    const me = await requireUserId()

    // "Just me" is a filter like any other, so it collapses into a people
    // filter -- and an explicit choice in the filter bar wins over it.
    //
    // One list rather than two paths: a single id and a set of them differed
    // only in shape, and keeping both would have meant the server deciding
    // which to believe when they disagreed.
    const picked = q.userIds && q.userIds.length ? [...q.userIds] : null
    const single = q.userId ?? (q.scope === 'mine' ? me : undefined)
    const userIds = picked ?? (single ? [single] : null)

    const summary = q.layout === 'summary'
    const prospects = q.kind === 'prospect'

    const data = await invokeFunction<unknown>('export-csv', {
      workspaceId: id,
      from: q.from,
      to: q.to,
      ...(summary ? { layout: 'summary', groupBy: q.groupBy ?? [] } : {}),
      ...(prospects ? { kind: 'prospect' } : {}),
      ...(userIds ? { userIds } : {}),
      ...(q.clientIds?.length
        ? { clientIds: [...q.clientIds] }
        : q.clientId
          ? { clientId: q.clientId }
          : {}),
      ...(q.projectIds?.length
        ? { projectIds: [...q.projectIds] }
        : q.projectId
          ? { projectId: q.projectId }
          : {}),
      ...(typeof q.billable === 'boolean' ? { billable: q.billable } : {}),
      ...(q.pricingModels?.length ? { pricingModels: [...q.pricingModels] } : {}),
    })

    const csv = typeof data === 'string' ? data : String(data ?? '')
    // Mirrors the name the function puts on Content-Disposition, which the
    // browser never sees because the body arrives through fetch, not a link.
    const prefix = prospects
      ? summary
        ? 'prospects_summary'
        : 'prospects'
      : summary
        ? 'summary'
        : 'timesheet'
    downloadFile(`${prefix}_${q.from}_to_${q.to}.csv`, csv, 'text/csv;charset=utf-8')
  },
}

/** Re-exported so callers that used these keep working. */
export { dayKey, secOfDay }
